import { api } from "./client";
import type {
  ActiveAlert,
  ActiveSiteAlert,
  AlertEvent,
  AlertRule,
  AlertRuleCreate,
  AlertRuleUpdate,
  AlertSubscription,
  AlertSubscriptionCreate,
  AlertSubscriptionUpdate,
  ComentarioDeEpisodio,
  DeviceStatus,
  Episodio,
  EpisodioDetalhe,
  EpisodioStatus,
  NotificationChannel,
  NotificationChannelCreate,
  NotificationChannelUpdate,
  Severity,
  TenantAlertSettings,
  TenantAlertSettingsUpdate,
} from "@/types";

// -- Tenant alert settings ---------------------------------------------------

export const tenantSettingsApi = {
  async get(tenantId?: string, signal?: AbortSignal): Promise<TenantAlertSettings> {
    const { data } = await api.get<TenantAlertSettings>("/tenant-alert-settings", {
      params: tenantId ? { tenant_id: tenantId } : undefined,
      signal,
    });
    return data;
  },
  async update(payload: TenantAlertSettingsUpdate, tenantId?: string): Promise<TenantAlertSettings> {
    const { data } = await api.patch<TenantAlertSettings>(
      "/tenant-alert-settings",
      payload,
      { params: tenantId ? { tenant_id: tenantId } : undefined },
    );
    return data;
  },
  /**
   * Pede o briefing agora, fora da sexta.
   *
   * Responde 202: aceito, ainda não entregue. Quem gera o briefing é o
   * container de analytics, que consome o pedido em até meio minuto.
   */
  async enviarBriefingAgora(tenantId?: string): Promise<void> {
    await api.post(
      "/tenant-alert-settings/briefing/enviar",
      null,
      { params: tenantId ? { tenant_id: tenantId } : undefined },
    );
  },
};

// -- Episódios ---------------------------------------------------------------

export interface EpisodiosQuery {
  tenant_id?: string;
  status?: EpisodioStatus;
  /** Filtrada no SERVIDOR: no cliente, filtraria só a página carregada. */
  severity?: Severity;
  /** ISO. Recorte por quando o problema COMEÇOU. */
  de?: string;
  ate?: string;
  pagina?: number;
  por_pagina?: number;
}

export interface PaginaDeEpisodios {
  items: Episodio[];
  total: number;
  pagina: number;
  por_pagina: number;
}

export const episodiosApi = {
  async list(q?: EpisodiosQuery): Promise<PaginaDeEpisodios> {
    const { data } = await api.get<PaginaDeEpisodios>("/alert-episodios", { params: q });
    return data;
  },
  async get(id: string): Promise<EpisodioDetalhe> {
    const { data } = await api.get<EpisodioDetalhe>(`/alert-episodios/${id}`);
    return data;
  },
  /** Encerra a OCORRÊNCIA. Endereça o episódio, não o (regra × ativo) —
   *  é o que funciona também no episódio cujo alerta já não está ativo. */
  async encerrar(id: string): Promise<void> {
    await api.post(`/alert-episodios/${id}/encerrar`);
  },
  async comentar(id: string, texto: string): Promise<ComentarioDeEpisodio> {
    const { data } = await api.post<ComentarioDeEpisodio>(
      `/alert-episodios/${id}/comentarios`, { texto },
    );
    return data;
  },
};

// -- Alert rules -------------------------------------------------------------

export const alertRulesApi = {
  async list(tenantId?: string): Promise<AlertRule[]> {
    const { data } = await api.get<AlertRule[]>("/alert-rules", {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    return data;
  },
  async create(payload: AlertRuleCreate, tenantId?: string): Promise<AlertRule> {
    const { data } = await api.post<AlertRule>("/alert-rules", payload, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    return data;
  },
  async update(id: string, payload: AlertRuleUpdate): Promise<AlertRule> {
    const { data } = await api.patch<AlertRule>(`/alert-rules/${id}`, payload);
    return data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/alert-rules/${id}`);
  },
};

// -- Notification channels ---------------------------------------------------

export const channelsApi = {
  async list(tenantId?: string, signal?: AbortSignal): Promise<NotificationChannel[]> {
    const { data } = await api.get<NotificationChannel[]>("/notification-channels", {
      params: tenantId ? { tenant_id: tenantId } : undefined,
      signal,
    });
    return data;
  },
  async create(payload: NotificationChannelCreate, tenantId?: string): Promise<NotificationChannel> {
    const { data } = await api.post<NotificationChannel>(
      "/notification-channels",
      payload,
      { params: tenantId ? { tenant_id: tenantId } : undefined },
    );
    return data;
  },
  async update(id: string, payload: NotificationChannelUpdate): Promise<NotificationChannel> {
    const { data } = await api.patch<NotificationChannel>(
      `/notification-channels/${id}`,
      payload,
    );
    return data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/notification-channels/${id}`);
  },
  async test(id: string): Promise<{ success: boolean; error: string | null }> {
    const { data } = await api.post<{ success: boolean; error: string | null }>(
      `/notification-channels/${id}/test`,
    );
    return data;
  },
};

// -- Subscriptions -----------------------------------------------------------

export const subscriptionsApi = {
  async list(channelId?: string, tenantId?: string): Promise<AlertSubscription[]> {
    const params: Record<string, string> = {};
    if (channelId) params.channel_id = channelId;
    if (tenantId) params.tenant_id = tenantId;
    const { data } = await api.get<AlertSubscription[]>("/alert-subscriptions", { params });
    return data;
  },
  async create(payload: AlertSubscriptionCreate): Promise<AlertSubscription> {
    const { data } = await api.post<AlertSubscription>("/alert-subscriptions", payload);
    return data;
  },
  async update(id: string, payload: AlertSubscriptionUpdate): Promise<AlertSubscription> {
    const { data } = await api.patch<AlertSubscription>(
      `/alert-subscriptions/${id}`, payload,
    );
    return data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/alert-subscriptions/${id}`);
  },
};

// -- Events + active alerts --------------------------------------------------

export interface AlertEventsQuery {
  equipment_id?: string;
  /**
   * Com `equipment_id`, traz também os eventos do SITE daquele equipamento.
   * A Camada 2 grava `equipment_id = NULL`; sem isto, a tela do ativo fica
   * muda numa queda de energia do site inteiro.
   */
  include_site_events?: boolean;
  severity?: string;
  event_type?: string;
  limit?: number;
  tenant_id?: string;
}

export const alertEventsApi = {
  async list(q?: AlertEventsQuery): Promise<AlertEvent[]> {
    const { data } = await api.get<AlertEvent[]>("/alert-events", { params: q });
    return data;
  },
  async active(tenantId?: string): Promise<ActiveAlert[]> {
    const { data } = await api.get<ActiveAlert[]>("/active-alerts", {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    return data;
  },
  /**
   * Sites em queda geral de energia (#162 Camada 2).
   * Endpoint separado porque site_power_outage não tem rule/equipment
   * específicos, é agregado cross-device por site.
   */
  async activeSiteAlerts(tenantId?: string): Promise<ActiveSiteAlert[]> {
    const { data } = await api.get<ActiveSiteAlert[]>("/active-site-alerts", {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    return data;
  },
  async acknowledge(ruleId: string, equipmentId: string): Promise<void> {
    await api.post(`/alert-state/${ruleId}/${equipmentId}/ack`);
  },
  /**
   * Fecha manualmente um alerta firing/acknowledged (#159 fix 4).
   * Usar quando o admin sabe que o problema não vai recuperar sozinho
   * (device removido, situação tratada offline) e não quer desabilitar
   * a rule inteira.
   */
  async close(ruleId: string, equipmentId: string): Promise<void> {
    await api.post(`/alert-state/${ruleId}/${equipmentId}/close`);
  },
};

// -- Device health -----------------------------------------------------------

export interface DeviceStatusFilters {
  site_id?: string;
  equipment_id?: string;
  tenant_id?: string;
}

export const deviceHealthApi = {
  async get(deviceId: string): Promise<DeviceStatus> {
    const { data } = await api.get<DeviceStatus>(`/devices/${deviceId}/health`);
    return data;
  },
  async list(filters?: DeviceStatusFilters): Promise<DeviceStatus[]> {
    const { data } = await api.get<DeviceStatus[]>("/device-statuses", {
      params: filters,
    });
    return data;
  },
};
